import { Provider } from '@angular/core';

import {
  TILE_ACCESS_PORT,
  type IVisualizer,
  type TileAccessPort,
} from '@jax-data-science/sci-image-visualizer';
import {
  RetinalLayerEngine,
  RetinalSegmenterService,
  StainAdapterEngine,
  YoloSegEngine,
  YoloSegmenterService,
} from '@jds/jit-extensions';

import { MainFacade } from './main/main.facade';
import type { ProcessingEngine } from './main/models/processing-pipeline/engine.model';
import { PROCESSING_ENGINES } from './main/models/processing-pipeline/processing-engines.contract';

/**
 * The JAX-only pipeline engines, swapped in for `jax-engines.provider.ts` by every
 * named build configuration.
 *
 * These three run private checkpoints, so they are the operations a public jit-ui
 * must not offer -- and since 0.3.0 they no longer live here at all: they are
 * implemented in `@jds/jit-extensions`, alongside the toolbar tools that run the
 * same checkpoints. This file is the whole coupling.
 *
 * WHY `satisfies` AND NOT AN IMPORTED CONTRACT
 * The plugin cannot import `ProcessingEngine` from jit-ui -- an application is not
 * a published package -- so it transcribes the contract instead, and TypeScript's
 * structural typing does the rest. These assertions are what make that safe: they
 * check each engine against *this* repo's definition, so if the two declarations
 * drift, this build fails rather than the dialog quietly losing an operation.
 * Publishing the pipeline contract would replace this with a plain import; until
 * then, do not remove the assertions.
 *
 * Note what is NOT here: `CellposeEngine`. Cellpose and SAM stay public -- they are
 * what the open demo runs -- so that engine belongs to the open set contributed by
 * `ProcessingPipelineModule`, even though it too loads a model in the browser.
 */
export function provideJaxPipelineEngines(): Provider[] {
  return [
    {
      provide: PROCESSING_ENGINES,
      multi: true,
      // Shares the toolbar tool's loaded YOLO checkpoint: one InferenceSession
      // for both surfaces, which is what the pipeline's old private loader cost.
      useFactory: (
        segmenters: YoloSegmenterService,
        facade: MainFacade,
        tileAccess: TileAccessPort,
      ) =>
        new YoloSegEngine(
          segmenters,
          () => visualizerOf(facade),
          tileAccess,
        ) satisfies ProcessingEngine,
      deps: [YoloSegmenterService, MainFacade, TILE_ACCESS_PORT],
    },
    {
      provide: PROCESSING_ENGINES,
      multi: true,
      // Likewise, and it matters more here: this checkpoint is ~590 MB, so
      // loading it once per surface was never acceptable.
      useFactory: (
        segmenters: RetinalSegmenterService,
        facade: MainFacade,
        tileAccess: TileAccessPort,
      ) =>
        new RetinalLayerEngine(
          segmenters,
          () => visualizerOf(facade),
          tileAccess,
        ) satisfies ProcessingEngine,
      deps: [RetinalSegmenterService, MainFacade, TILE_ACCESS_PORT],
    },
    {
      provide: PROCESSING_ENGINES,
      multi: true,
      // Stain has no toolbar counterpart to share a model with, and owns its own
      // adapters. It imports its libraries lazily, so contributing it downloads
      // nothing.
      useFactory: () => new StainAdapterEngine() satisfies ProcessingEngine,
    },
  ];
}

/**
 * The live visualizer, or null.
 *
 * Resolved per call rather than injected: the active diagram changes as the user
 * switches images, and an engine only needs the viewer at the moment it commits
 * regions. `getDiagram()` is typed `any`, so this is also the one place that
 * narrows it back to the library's published contract.
 */
function visualizerOf(facade: MainFacade): IVisualizer | null {
  return facade.getDiagram()?.plotService ?? null;
}
